<script setup lang="ts">
import {
    faAngleLeft,
    faAngleRight,
    faArrowRotateLeft,
} from '@fortawesome/pro-regular-svg-icons';
import AlternativeSuggestion from './AlternativeSuggestion.vue';
import { type TripSearchForm } from '@search/trip/TripSearchForm';

interface IProps {
    disableActions?: boolean;
}
const { disableActions = false } = defineProps<IProps>();

const itineraryStore = useItineraryStore();
const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
const currentModule = useCurrentItineraryModule();
const router = useRouter();
const route = useRoute();
const { showChangeDateSuggestion, showSearchSuggestion } =
    storeToRefs(itineraryStore);
const { emptyListReason } = moduleToRefs(currentModule.value);
const image = computed(() => {
    if (itineraryStore.noResults) {
        return emptyListReason.value.image;
    }
    return '/img/no-result.png';
});
const title = computed(() => {
    if (itineraryStore.noResults) {
        return emptyListReason.value.title;
    }
    return 'سرویسی با فیلترهای مشخص شده یافت نشد!';
});
const notFoundText = computed(() => {
    if (itineraryStore.noResults) {
        return emptyListReason.value.notFoundText;
    }
    return 'لطفاً فیلترهای دیگری را امتحان کنید.';
});
const idOfElement = computed(
    () => emptyListReason.value.idOfElement || 'no-result'
);
const prevDisabled = computed(() =>
    dayConfig
        .gregory()
        .isSameOrAfter(currentSearchForm.value.departureDate, 'date')
);
const goYesterday = () => {
    currentSearchForm.value.prevDay(({ query, path }) => {
        router.replace({
            path,
            query: { ...route.query, ...query },
        });
    });
};
const goTomorrow = () => {
    currentSearchForm.value.nextDay(({ query, path }) => {
        router.replace({
            path,
            query: { ...route.query, ...query },
        });
    });
};
</script>

<template>
    <div :id="idOfElement" class="empty-list-container">
        <img class="empty-list-image" :src="image" alt="No results" />
        <mr-typography size="large" weight="700" align="center">
            <slot name="message">
                {{ title }}
                <br />
                {{ notFoundText }}
            </slot>
        </mr-typography>
        <div
            v-if="!disableActions && showChangeDateSuggestion"
            class="calendar-buttons"
        >
            <mr-button
                variant="transparent"
                :right-icon="faAngleRight"
                :disabled="prevDisabled"
                @click="goYesterday"
            >
                روز قبل
            </mr-button>
            <mr-button
                variant="transparent"
                :left-icon="faAngleLeft"
                @click="goTomorrow"
            >
                روز بعد
            </mr-button>
        </div>
        <AlternativeSuggestion
            v-if="showSearchSuggestion"
            class="alternatives"
        />
        <slot name="no-result">
            <mr-button
                v-if="!disableActions"
                link
                class="back-button"
                href="/"
                :right-icon="faArrowRotateLeft"
            >
                بازگشت به صفحه اصلی
            </mr-button>
        </slot>
    </div>
</template>

<style lang="scss" scoped>
.empty-list-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding-top: 24px;
    .empty-list-image {
        width: 216px;
        height: 150px;
    }

    .calendar-buttons {
        display: flex;
        gap: 24px;
    }
    .alternatives {
        margin-top: 20px;
    }
}
</style>
